import React, { useState, useRef } from "react";

const DEBOUNCE_MS = 3000;
const REQUEST_TIMEOUT_MS = 30000;

const TOPIC_OPTIONS = [
  "I'd like early access to FeraBio",
  "I'd like a platform walkthrough",
  "I have a pricing question",
  "I want to discuss a pilot",
  "Something else",
];

const REFERRAL_OPTIONS = [
  "Conference or event",
  "Direct outreach from the FeraBio team",
  "Colleague or collaborator referral",
  "Web search (Google, Bing, etc.)",
  "Social media (LinkedIn, X, etc.)",
  "Scientific publication or preprint",
  "Podcast, webinar, or talk",
  "Other",
];

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  topic: "",
  referral: "",
  message: "",
  consent: false,
};

const WaitlistPage = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [successType, setSuccessType] = useState("waitlist");
  const lastSubmitTimeRef = useRef(0);
  const cardRef = useRef(null);
  const cardHeightRef = useRef(null);

  const handleFieldChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    if (Date.now() - lastSubmitTimeRef.current < DEBOUNCE_MS) {
      return;
    }

    if (!formData.email.trim()) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    if (!formData.topic) {
      setErrorMessage("Please select a topic.");
      return;
    }

    if (!formData.consent) {
      setErrorMessage("Please confirm consent before submitting.");
      return;
    }

    const configuredApiUrl = process.env.REACT_APP_WAITLIST_API_URL;
    const apiUrl = configuredApiUrl || "/api/waitlist";

    const payload = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      topic: formData.topic,
      referralSource: formData.referral.trim(),
      message: formData.message.trim(),
      consent: formData.consent,
      submittedAt: new Date().toISOString(),
      source: "fera_bio_front_end_waitlist",
    };

    lastSubmitTimeRef.current = Date.now();
    setIsSubmitting(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (data.error === "already_registered") {
          setErrorMessage(
            data.message ||
              "You're already on the waitlist! We'll reach out when access opens."
          );
          return;
        }
        if (data.error === "service_unavailable") {
          setErrorMessage(
            data.message ||
              "The request could not be completed. Please try again later."
          );
          return;
        }
        if (data.error === "form_error" && data.message) {
          setErrorMessage(data.message);
          return;
        }
        setErrorMessage(
          data.message || "Something went wrong. Please try again."
        );
        return;
      }

      if (cardRef.current) {
        cardHeightRef.current = cardRef.current.offsetHeight;
      }
      setSuccessType(data.successType || "waitlist");
      setSubmitted(true);
      setFormData(INITIAL_FORM);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === "AbortError") {
        setErrorMessage(
          "The request took too long. Please try again later."
        );
        return;
      }
      if (!configuredApiUrl) {
        setErrorMessage(
          "No API URL is configured yet. This attempted a local placeholder POST to /api/waitlist so you can inspect the payload in Network."
        );
      } else {
        setErrorMessage(
          "The request could not be completed. Please try again later."
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="waitlist-page">
      <div className="container waitlist-page-grid">
        <div className="waitlist-page-left">
          <p className="waitlist-page-eyebrow">Join Waitlist</p>
          <h1 className="waitlist-page-title">
            Let's build faster nanopore workflows together
          </h1>
          <p className="waitlist-page-subtitle">
            Tell us about your sequencing workflows and we will follow up about
            early access, pilots, and onboarding.
          </p>

          <ul className="waitlist-page-benefits">
            <li>Save time with cloud-native basecalling and analysis</li>
            <li>Improve confidence with built-in quality controls</li>
            <li>Collaborate with your team from one shared workspace</li>
            <li>Scale without managing infrastructure</li>
          </ul>
        </div>

        <div className="waitlist-page-right">
          <div
            ref={cardRef}
            className="waitlist-page-card"
            style={
              submitted && cardHeightRef.current
                ? { minHeight: cardHeightRef.current }
                : undefined
            }
          >
            {submitted ? (
              <div className="waitlist-page-success">
                {successType === "waitlist" ? (
                  <>
                    <h2>You're on the waitlist.</h2>
                    <p>
                      We'll be in touch as we open access to more users.
                    </p>
                  </>
                ) : (
                  <>
                    <h2>Thank you for reaching out!</h2>
                    <p>
                      Our team has received your message and will follow up as soon as we're able to.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <>
                <h2 className="waitlist-page-card-title">
                  Talk to the FeraBio team
                </h2>
                <form className="waitlist-page-form" onSubmit={handleSubmit}>
                  {errorMessage ? (
                    <p className="waitlist-page-error">{errorMessage}</p>
                  ) : null}
                  <div className="waitlist-page-row two-col">
                    <div className="field-group">
                      <label htmlFor="firstName">First Name</label>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        value={formData.firstName}
                        onChange={handleFieldChange}
                      />
                    </div>
                    <div className="field-group">
                      <label htmlFor="lastName">Last Name</label>
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        value={formData.lastName}
                        onChange={handleFieldChange}
                      />
                    </div>
                  </div>
                  <div className="waitlist-page-row two-col">
                    <div className="field-group">
                      <label htmlFor="email">Email*</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleFieldChange}
                      />
                    </div>
                    <div className="field-group">
                      <label htmlFor="phone">Phone Number</label>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleFieldChange}
                      />
                    </div>
                  </div>
                  <div className="field-group">
                    <label htmlFor="topic">Topic*</label>
                    <select
                      id="topic"
                      name="topic"
                      required
                      value={formData.topic}
                      onChange={handleFieldChange}
                    >
                      <option value="" disabled>
                        Select one...
                      </option>
                      {TOPIC_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field-group">
                    <label htmlFor="referral">How did you hear about us?</label>
                    <select
                      id="referral"
                      name="referral"
                      value={formData.referral}
                      onChange={handleFieldChange}
                    >
                      <option value="">Select one...</option>
                      {REFERRAL_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="4"
                      value={formData.message}
                      onChange={handleFieldChange}
                    />
                  </div>
                  <label className="waitlist-page-consent">
                    <input
                      name="consent"
                      type="checkbox"
                      required
                      checked={formData.consent}
                      onChange={handleFieldChange}
                    />
                    <span>
                      I agree to be contacted about FeraBio early access.
                    </span>
                  </label>
                  <button
                    type="submit"
                    className="waitlist-page-submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Request"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WaitlistPage;
